/**
 * 提交历史列表组件
 * 展示工作流的历史提交记录列表
 */

import { forwardRef, useImperativeHandle } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { History, Inbox, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useSubmissionHistory } from '@/hooks/useSubmissionHistory'
import { SubmissionHistoryItem } from './SubmissionHistoryItem'

/**
 * SubmissionHistory 组件暴露的方法
 */
export interface SubmissionHistoryRef {
  /** 刷新历史记录 */
  refresh: () => void
}

/**
 * 提交历史列表组件属性
 */
interface SubmissionHistoryProps {
  /** 工作流 ID */
  workflowId: string | undefined
  /** 最大显示条数 */
  maxItems?: number
  /** 自定义类名 */
  className?: string
  /** 是否已登录（未登录时不请求数据，显示提示） */
  isLoggedIn?: boolean
}

/**
 * 列表动画变体
 */
const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
}

/**
 * 列表项动画变体
 */
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
}

/**
 * 加载骨架屏组件
 * @returns 加载骨架屏 ReactElement
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border bg-muted/50 p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="h-3 w-16 rounded bg-muted" />
            <div className="h-4 w-12 rounded-full bg-muted" />
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div className="h-7 rounded-lg bg-muted" />
            <div className="h-7 rounded-lg bg-muted" />
          </div>
          <div className="h-1.5 rounded bg-muted" />
        </div>
      ))}
    </div>
  )
}

/**
 * 空状态组件
 * @returns 空状态 ReactElement
 */
function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Inbox className="mb-3 h-12 w-12 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">暂无提交记录</p>
      <p className="mt-1 text-xs text-muted-foreground/70">
        提交任务后，历史记录将显示在这里
      </p>
    </div>
  )
}

/**
 * 未登录提示组件
 * @returns 未登录提示 ReactElement
 */
function NotLoggedInState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Inbox className="mb-3 h-12 w-12 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">登录后查看历史记录</p>
      <p className="mt-1 text-xs text-muted-foreground/70">
        登录账号后即可查看您的提交记录
      </p>
    </div>
  )
}

/**
 * 提交历史列表组件
 * @param props - 组件属性
 * @param ref - 组件 ref
 * @returns 提交历史列表 ReactElement
 */
export const SubmissionHistory = forwardRef<SubmissionHistoryRef, SubmissionHistoryProps>(
  function SubmissionHistory(
    { workflowId, maxItems = 10, className, isLoggedIn = true },
    ref
  ): ReactElement {
    const { submissions, isLoading, total, refresh } = useSubmissionHistory(
      workflowId,
      maxItems,
      isLoggedIn
    )

    // 暴露 refresh 方法给父组件
    useImperativeHandle(
      ref,
      () => ({
        refresh,
      }),
      [refresh]
    )

    // 限制显示条数
    const displayedSubmissions = submissions.slice(0, maxItems)
    const displayTotal = Math.min(total, maxItems)

    return (
      <div
        className={cn(
          'flex flex-col rounded-2xl border bg-card shadow-sm',
          className
        )}
      >
        {/* 标题栏 */}
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">历史提交</h3>
            {displayTotal > 0 && (
              <Badge variant="secondary" className="text-xs">
                {displayTotal} 条
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">
              仅显示最近 {String(maxItems)} 条
            </span>
            <Link
              to="/records/generations"
              className="text-xs text-primary hover:underline"
            >
              查看全部 →
            </Link>
          </div>
        </div>

        {/* 列表内容 */}
        <div className="overflow-y-auto p-4">
          {!isLoggedIn ? (
            <NotLoggedInState />
          ) : isLoading ? (
            <LoadingSkeleton />
          ) : displayedSubmissions.length === 0 ? (
            <EmptyState />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                className="space-y-2"
                variants={listVariants}
                initial="hidden"
                animate="visible"
              >
                {displayedSubmissions.map((record) => (
                  <motion.div key={record.id} variants={itemVariants}>
                    <SubmissionHistoryItem record={record} />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}

          {/* 加载更多提示 */}
          {isLoading && displayedSubmissions.length > 0 && (
            <div className="mt-4 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-xs text-muted-foreground">
                加载中...
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
)
